import React from 'react';
import type { Report, ReportDetailLabels } from '../../shared/types/reports';
import { VisualSummary } from './reports/VisualSummary';
import { MoodDistributionChart } from './reports/MoodDistributionChart';
import { MoodComparison } from './reports/MoodComparison';
import { GoalProgressRings } from './reports/GoalProgressRings';
import { Achievements } from './reports/Achievements';
import { ActivityComparison } from './reports/ActivityComparison';
import { AiReportSection } from './reports/AiReportSection';

export interface ReportDetailPageProps {
  report: Report;
  reportsListUrl: string;
  aiReportFullUrl: string;
  onSubmitFeedback: (feedbackType: 'dislike' | 'like' | 'super_like', comments?: string) => Promise<void>;
  onDeleteFeedback: () => Promise<void>;
  labels: ReportDetailLabels;
}

export function ReportDetailPage({
  report,
  reportsListUrl,
  aiReportFullUrl,
  onSubmitFeedback,
  onDeleteFeedback,
  labels,
}: ReportDetailPageProps) {
  const periodLabel = report.periodType === 'week' ? labels.weekOf : labels.monthOf;
  const periodTypeBadge = report.periodType === 'week' ? 'Weekly' : 'Monthly';

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const hasData = report.currentPeriodStats.moodCount > 0 ||
    (report.previousPeriodStats?.moodCount || 0) > 0;

  return (
    <div className="report-detail-page">
      {/* Header Section */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
        <h4 className="mb-0">
          Report
          <span className="badge bg-secondary ms-2">{periodTypeBadge}</span>
        </h4>
        <a href={reportsListUrl} className="btn btn-sm btn-outline-secondary">
          <i className="bi bi-arrow-left me-1"></i>
          {labels.backToReports}
        </a>
      </div>

      {/* Period Info Card */}
      <div className="mb-4">
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <p className="mb-1">
              <strong>Period:</strong> {formatDate(report.periodStart)} - {formatDate(report.periodEnd)}
            </p>
            <p className="mb-0 text-muted">
              <small>
                <i className="bi bi-calendar-check me-1"></i>
                Generated: {formatDateTime(report.generatedAt)}
              </small>
            </p>
          </div>
        </div>
      </div>

      {!hasData ? (
        <div className="alert alert-info">
          Not enough data to show reports
        </div>
      ) : (
        <>
          {/* Visual Summary Hero Card */}
          <VisualSummary
            currentPeriodStats={report.currentPeriodStats}
            previousPeriodStats={report.previousPeriodStats}
            moodChange={report.moodChange}
            labels={labels}
          />

          {/* AI Report Section */}
          <AiReportSection
            aiReport={report.aiReport}
            aiReportFullUrl={aiReportFullUrl}
            onSubmitFeedback={onSubmitFeedback}
            onDeleteFeedback={onDeleteFeedback}
            labels={labels}
          />

          {/* Mood Distribution Chart */}
          <div className="row g-3 mb-4">
            <div className="col-12">
              <MoodDistributionChart
                currentPeriodStats={report.currentPeriodStats}
                labels={labels}
              />
            </div>
          </div>

          {/* Mood Comparison Cards */}
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-white border-0">
                  <h5 className="mb-0">{labels.comparison} - {labels.averageMood}</h5>
                </div>
                <div className="card-body">
                  <MoodComparison
                    currentPeriodStats={report.currentPeriodStats}
                    previousPeriodStats={report.previousPeriodStats}
                    periodType={report.periodType}
                    labels={labels}
                  />
                </div>
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-white border-0">
                  <h5 className="mb-0">{labels.comparison} - {labels.moodEntries}</h5>
                </div>
                <div className="card-body">
                  <MoodCountComparison
                    currentPeriodStats={report.currentPeriodStats}
                    previousPeriodStats={report.previousPeriodStats}
                    periodType={report.periodType}
                    moodCountChange={report.moodCountChange}
                    labels={labels}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Goals Section */}
          {report.currentPeriodStats.goals.length > 0 && (
            <div className="mb-4">
              <GoalProgressRings
                goals={report.currentPeriodStats.goals}
                labels={labels}
              />
            </div>
          )}

          {/* Achievements Section */}
          {report.currentPeriodStats.achievements.length > 0 && (
            <div className="mb-4">
              <Achievements
                achievements={report.currentPeriodStats.achievements}
                labels={labels}
              />
            </div>
          )}

          {/* Activity Details */}
          <div className="mb-4">
            <ActivityComparison
              currentPeriodStats={report.currentPeriodStats}
              previousPeriodStats={report.previousPeriodStats}
              periodType={report.periodType}
              labels={labels}
            />
          </div>
        </>
      )}
    </div>
  );
}

// Mood Count Comparison Component (inline)
interface MoodCountComparisonProps {
  currentPeriodStats: Report['currentPeriodStats'];
  previousPeriodStats: Report['previousPeriodStats'];
  periodType: 'week' | 'month';
  moodCountChange: number | null;
  labels: ReportDetailLabels;
}

function MoodCountComparison({
  currentPeriodStats,
  previousPeriodStats,
  periodType,
  moodCountChange,
  labels,
}: MoodCountComparisonProps) {
  const periodName = periodType === 'week' ? 'Week' : 'Month';

  const formatPeriod = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return `${startDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  };

  return (
    <div className="row g-3">
      {/* Previous Period */}
      <div className="col-12 col-md-6">
        <div className="text-center p-3 bg-light rounded">
          <h6 className="text-muted mb-1">{labels.previousPeriod} {periodName}</h6>
          {previousPeriodStats && (
            <p className="text-muted small mb-2">
              {formatPeriod(previousPeriodStats.periodStart, previousPeriodStats.periodEnd)}
            </p>
          )}
          <div className="display-5 fw-bold text-primary">
            {previousPeriodStats?.moodCount ?? 0}
          </div>
        </div>
      </div>

      {/* Current Period */}
      <div className="col-12 col-md-6">
        <div className="text-center p-3 bg-light rounded">
          <h6 className="text-muted mb-1">{labels.currentPeriod} {periodName}</h6>
          <p className="text-muted small mb-2">
            {formatPeriod(currentPeriodStats.periodStart, currentPeriodStats.periodEnd)}
          </p>
          <div className="display-5 fw-bold text-success">
            {currentPeriodStats.moodCount}
          </div>
          {moodCountChange !== null && moodCountChange !== 0 && (
            <div className="mt-2">
              {moodCountChange > 0 ? (
                <span className="badge bg-success">
                  <i className="bi bi-arrow-up me-1"></i>
                  {moodCountChange}
                </span>
              ) : (
                <span className="badge bg-danger">
                  <i className="bi bi-arrow-down me-1"></i>
                  {Math.abs(moodCountChange)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReportDetailPage;
